import React, { useState, useEffect } from 'react';
import * as Types from '../../services/api/api.types';
import ProductCard from '../products/ProductCard';

interface RotatingProductDisplayProps {
  products: Types.ProductFull[];
  rotationInterval?: number;
  autoRotate?: boolean;
  title: string;
  loading?: boolean;
}

const RotatingProductDisplay: React.FC<RotatingProductDisplayProps> = ({
  products,
  rotationInterval = 5000,
  autoRotate = true,
  title,
  loading = false,
}) => {
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);

  const calculateBatches = (items: Types.ProductFull[]) => {
    const batchSize = 4;
    const completeProductSets = Math.floor(items.length / batchSize) * batchSize;
    const validProducts = items.slice(0, completeProductSets);

    const batches: Types.ProductFull[][] = [];
    for (let i = 0; i < validProducts.length; i += batchSize) {
      batches.push(validProducts.slice(i, i + batchSize));
    }
    return batches;
  };

  const batches = calculateBatches(products);
  const totalBatches = batches.length;

  useEffect(() => {
    if (!autoRotate || totalBatches <= 1) return;

    const interval = setInterval(() => {
      setCurrentBatchIndex((prevIndex) => (prevIndex + 1) % totalBatches);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [autoRotate, rotationInterval, totalBatches]);

  const handleDotClick = (index: number) => {
    setCurrentBatchIndex(index);
  };

  const handlePrevious = () => {
    setCurrentBatchIndex((prevIndex) =>
      prevIndex === 0 ? totalBatches - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentBatchIndex((prevIndex) =>
      (prevIndex + 1) % totalBatches
    );
  };

  if (loading) {
    return (
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-center mb-6 uppercase">{title}</h2>
        <div className="flex justify-center">
          <p>Зареждане...</p>
        </div>
      </section>
    );
  }

  if (batches.length === 0) {
    return null;
  }

  const currentBatch = batches[currentBatchIndex] || [];

  return (
    <section className="mb-12">
      <h2 className="text-2xl font-bold text-center mb-6 uppercase">{title}</h2>

      <div className="relative">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {currentBatch.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {totalBatches > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full shadow-lg p-2 hover:bg-gray-100 transition-colors"
              aria-label="Previous batch"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full shadow-lg p-2 hover:bg-gray-100 transition-colors"
              aria-label="Next batch"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="flex justify-center mt-6 space-x-2">
              {batches.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentBatchIndex
                      ? 'bg-primary-500'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to batch ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default RotatingProductDisplay;